import API, { graphqlOperation } from "@aws-amplify/api";
import useAPI from "@toothfairy/shared-api/useApi";

import AppForms from "@toothfairy/shared-ui/AppForms";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppToast from "@toothfairy/shared-ui/AppToast";
import AppUser from "@toothfairy/shared-ui/AppUser";
import AppUtilities from "@toothfairy/shared-ui/AppUtilities";
import dayjs from "dayjs";
import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { isSSO, reservedConfigs, wasWorkspaceJustCreated } from "../../../App";
import envConfig from "../../../envConfig";
import API_Consumption from "../../API/API_Consumption";
import Agents from "../../API/Agents";
import entitiesHelper from "../../API/EntitiesAI/entitiesHelper";
import Files from "../../API/Files";
import workspacesHelper from "../../API/Workspaces/workspacesHelper";
import Constants, { defaultConsumptionObject } from "../../Constants";
import useWorkspaces from "../../Hooks/useWorkspaces";
import useRechargeRedirect from "../../Hooks/useRechargeRedirect";
import { useHistory } from "../../Router";
import { createUserWorkspaceType } from "../../graphql/mutations";
import {
  getWorkspace,
  listSubscriptionBaseConfigs,
  listSubscriptionPricingPlans,
} from "../../graphql/queries";
import { UserType } from "../../models";
import {
  normalizeBlockedModels,
  blockedModelsToIds,
} from "../../Utils/modelRestrictions";
import WorkspacesScreen from "./WorkspacesScreen";
import {
  DEFAULT_NEW_WORKSPACE_SEATS,
  ENABLE_CONSUMPTION_BILLING,
  getBillingPeriodParams,
  getTotalUoiFromCosts,
} from "../../Constants/billing";
import { makeGraphQLRequest } from "../../API";

const MAX_CHILDREN_BY_SUBSCRIPTION = {
  business: 5,
  enterprise: 50,
};

export const formValidationSchema = AppForms.yup.object().shape({
  workspaceName: AppForms.yup.string().required().label("Workspace name"),
  // workspaceType: AppForms.yup
  //   .string()
  //   .required()
  //   .label("Workspace intent structure"),
  workspaceMed: AppForms.yup
    .boolean()
    .required()
    .label("Workspace medical setting"),
  logo: AppForms.yup.string().nullable().label("Logo"),
  blockedModels: AppForms.yup.array().nullable().label("Blocked models"),
});

const WorkspacesScreenContainer = () => {
  const { t } = useTranslation();
  const { state, dispatch } = AppStateManager.useAppStateManager();
  const [targetPlan, setTargetPlan] = useState(false);
  const [paymentReattempted, setPaymentReattended] = useState(false);
  const [isReattemptingPayment, setIsReattemptingPayment] = useState(false);
  const [workspaceMutationInProgress, setWorkspaceMutationInProgress] =
    useState(false);
  const { user } = AppUser.useUser();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedWorkspace, setSelectedWorkspace] = useState();
  const [modalContentKey, setModalContentKey] = useState();
  const [workspaceLogo, setWorkspaceLogo] = useState(null);

  const eligibleParentWorkspaces = useMemo(() => {
    return (state.workspaces || []).filter((ws) => {
      const hasRootOrSuperAdminAccess = (
        ws?.usersAccessLevel?.items || []
      ).some(
        (entry) =>
          entry?.userID === user.id &&
          !entry?._deleted &&
          ["Root", "SuperAdmin"].includes(entry?.userType)
      );
      if (
        !hasRootOrSuperAdminAccess ||
        ws.parentWorkspaceID ||
        !["business", "enterprise"].includes(ws.subscriptionType?.toLowerCase())
      ) {
        return false;
      }

      const subType = ws.subscriptionType?.toLowerCase();
      const maxChildren = MAX_CHILDREN_BY_SUBSCRIPTION[subType] || 0;
      const currentChildren = (state.workspaces || []).filter(
        (child) => child.parentWorkspaceID === ws.id
      ).length;

      return currentChildren < maxChildren;
    });
  }, [state.workspaces, user.id]);
  const [fileUploadInProgress, setFileUploadInProgress] = useState(false);
  const [fileUploadProgress, setFileUploadProgress] = useState(0);
  const [workspaceLogoFilename, setWorkspaceLogoFilename] = useState(null);
  const silentRefreshTimerRef = useRef(null);

  // Exact UoI consumption per workspace card, sourced from /billing/monthCosts —
  // the SAME call the dashboard makes. The `aiConsumption.totalUoI` counter on
  // the GraphQL record is stale/misleading, so while a card is `loading` the UI
  // shows a translucent skeleton bar and only renders the numbers once the exact
  // value has been fetched (`loaded`). Token-less workspaces (still configuring)
  // stay on the skeleton — the billing API can't be read without a token.
  const [uoiCosts, setUoiCosts] = useState({});
  const uoiRequestedRef = useRef(new Set());

  useEffect(() => {
    if (state?.workspacesLoading) return;
    (state?.workspaces || []).forEach((ws) => {
      const id = ws?.id;
      if (!id || uoiRequestedRef.current.has(id)) return;
      uoiRequestedRef.current.add(id);
      setUoiCosts((prev) => ({
        ...prev,
        [id]: { status: "loading", consumed: 0 },
      }));
      if (!ws?.workspaceToken) return;
      const now = dayjs();
      API_Consumption.getCosts({
        workspaceId: id,
        ...(getBillingPeriodParams(ws, now) || {
          month: now.get("month") + 1,
          year: now.get("year"),
        }),
        token: ws.workspaceToken,
        includeChildren: true,
        excludePerMethodperDay: true,
      })
        .then((res) => {
          const data = res?.data || res;
          if (data) {
            setUoiCosts((prev) => ({
              ...prev,
              [id]: { status: "loaded", consumed: getTotalUoiFromCosts(data) },
            }));
          }
        })
        .catch((error) =>
          console.error("Error loading workspace UoI consumption", error)
        );
    });
  }, [state?.workspacesLoading, state?.workspaces]);

  useEffect(() => {
    return () => {
      if (silentRefreshTimerRef.current) {
        clearTimeout(silentRefreshTimerRef.current);
      }
    };
  }, []);
  const {
    generateBaseWorkspaceTrainingConfig,
    generateWorkspaceParsingConfig,
    generateWorkspaceNotificationConfig,
    getBaseWorkspaceConfig,
    isWorkspaceDisabled,
  } = useWorkspaces();
  const {
    data,
    loading,
    apiRequest: updateUsageForWorkspaceRequest,
    response,
  } = useAPI(API_Consumption.updateUsageForWorkspace, envConfig);
  const history = useHistory();
  // Detect a Stripe Checkout success/cancel redirect (?recharged=&session_id=)
  // and synchronously confirm the recharge so the balance is current — otherwise
  // the user could be hard-blocked from creating a workspace right after paying.
  useRechargeRedirect();
  const [initialValues, setInitialValues] = useState(
    Constants.initialWorkspaceValues
  );
  const _valuesRef = useRef({});
  const _prevEnforceRegionalModelsRef = useRef(undefined);
  const AppToasty = AppToast.useToast();

  const identifyWorkspaceByID = (id) => {
    return state.workspaces.map((u) => u.id).indexOf(id);
  };

  const getWorkspaceLogoUrl = async (workspaceId, imagePath) => {
    if (!imagePath || !workspaceId) {
      setWorkspaceLogo(null);
      return;
    }
    try {
      const _url = await Files.downloadUrlGeneration({
        filename: `imported-image/${workspaceId}/${imagePath}`,
        context: "pdf",
        workspaceid: workspaceId,
      });
      if (_url?.url) {
        setWorkspaceLogo(_url.url);
      }
    } catch (error) {
      console.log("Error getting workspace logo URL", error);
      setWorkspaceLogo(null);
    }
  };

  const handleWorkspaceSelection = (id) => {
    const workspace = state?.workspaces.find((u) => u?.id == id);
    if (workspace?.isMFAEnabled && user?.isMFAEnabled != true) {
      dispatch("SET_PAGE_NOT_FOUND", true);
      history.push("/mfa_required");
    } else if (!isWorkspaceDisabled(workspace)) {
      dispatch("SET_ACTIVE_WORKSPACE_ID", id);
      history.push(`/workspaces/${id}/home`);
    } else if (
      !workspace?.parentWorkspaceID &&
      workspace?.userID === user?.id
    ) {
      history.push("/profile/billing");
    } else if (workspace?.parentWorkspaceID) {
      AppToasty.show(t("workspaces.contactRootAdminOfParentWorkspace"), {
        type: "danger",
      });
    } else {
      AppToasty.show(t("workspaces.contactRootAdminToReactivate"), {
        type: "danger",
      });
    }
  };
  const handlePaymentAttempt = async (selectedWorkspace) => {
    try {
      // Sub-workspaces inherit billing from their root parent, so the payment
      // retry must always target the root workspace (the one that owns the
      // subscription), never the child.
      const rootWorkspace = selectedWorkspace?.parentWorkspaceID
        ? state.workspaces?.find(
            (ws) => ws.id === selectedWorkspace.parentWorkspaceID
          ) || selectedWorkspace
        : selectedWorkspace;
      setIsReattemptingPayment(rootWorkspace.id);

      // Consumption billing: there is no subscription payment to retry — the
      // workspace was paused by the consumption watcher for lack of credit.
      // Re-enable it immediately if the owner now has credits; the backend
      // returns a machine-readable `reason` when it cannot.
      if (ENABLE_CONSUMPTION_BILLING) {
        const result = await API_Consumption.resumeConsumptionWorkspace({
          workspaceId: rootWorkspace.id,
        });
        const body = result?.data ?? result;
        if (body?.resumed === true) {
          AppToasty.show(t("workspaces.workspaceResumed"), {
            type: "success",
          });
          setPaymentReattended(true);
          try {
            const allWorkspaces = await workspacesHelper.getWorkspacesByUserID(
              user.id
            );
            const freshWorkspaces = allWorkspaces
              ?.map((u) => u?.data?.getWorkspace)
              ?.filter((u) => u && u?._deleted !== true);
            if (freshWorkspaces) {
              dispatch("SET_WORKSPACES", freshWorkspaces);
            }
          } catch (refreshError) {
            console.error(
              "Error refreshing workspaces after reactivation",
              refreshError
            );
          }
        } else if (body?.reason === "no_credit") {
          AppToasty.show(t("workspaces.workspaceResumeNeedCredits"), {
            type: "danger",
          });
        } else if (body?.reason === "already_active") {
          AppToasty.show(t("workspaces.workspaceAlreadyActive"), {
            type: "success",
          });
        } else {
          AppToasty.show(t("workspaces.workspaceResumeFailed"), {
            type: "danger",
          });
        }
        return;
      }

      const result = await API_Consumption.collectPaymentsFromSubscription({
        workspaceId: rootWorkspace.id,
        subscriptionId: rootWorkspace.subscriptionId,
      });
      if (result?.status == 200) {
        AppToasty.show(t("workspacePaymentSubmitted"), { type: "success" });
        setPaymentReattended(true);
      } else AppToasty.show(t("workspacePaymentFailed"), { type: "danger" });
    } catch (error) {
      if (ENABLE_CONSUMPTION_BILLING) {
        AppToasty.show(t("workspaces.workspaceResumeFailed"), {
          type: "danger",
        });
      } else {
        AppToasty.show(t("workspacePaymentFailed"), { type: "danger" });
      }
      console.error("Error while paying for the workspace", error);
    } finally {
      setIsReattemptingPayment(null);
    }
  };
  const openNewWorkspaceFlow = () => {
    setModalContentKey("NEW");
    setInitialValues({
      workspaceName: "",
      workspaceMed: false,
      baseModel: "NONE",
      domain: "DEFAULT",
      subscriptionType: ENABLE_CONSUMPTION_BILLING ? "business" : "pro",
      maxUsers: DEFAULT_NEW_WORKSPACE_SEATS,
      parentWorkspaceID: null,
      billingProvider: "STRIPE",
      supportHours: 0,
      reservedComputeAllocation: false,
    });
    setIsModalOpen(true);
  };

  const handleWorkSpaceCreation = () => {
    wasWorkspaceJustCreated(false);
    // The consumption wizard always opens on "new workspace". If the user has
    // no credits, a recharge step is shown inside the wizard (not a blocking
    // gate that prevents the wizard from appearing).
    openNewWorkspaceFlow();
  };
  useEffect(() => {
    const fetchDataForConfig = async () => {
      const result = await makeGraphQLRequest(
        graphqlOperation(listSubscriptionPricingPlans, {})
      );

      const config = result?.data?.listSubscriptionPricingPlans;
      const result2 = await makeGraphQLRequest(
        graphqlOperation(listSubscriptionBaseConfigs, {})
      );
      const config2 = result2?.data?.listSubscriptionBaseConfigs;
      reservedConfigs({
        ...reservedConfigs(),
        plans: config?.items,
        workspaceBaseConfigs: config2?.items,
      });
    };
    fetchDataForConfig();
    console.log("BASE CONFIGS", reservedConfigs());
  }, []);
  useEffect(() => {
    if (!isSSO() && state.workspaces?.length == 0 && !isModalOpen) {
      if (ENABLE_CONSUMPTION_BILLING) {
        // Consumption model: always open the creation wizard for a brand-new
        // user. The wizard itself shows a recharge step if there are no credits.
        openNewWorkspaceFlow();
      } else if (user?.stripeCustomerData?.balance / -100 > 4.99) {
        setModalContentKey("NEW");
        setInitialValues({
          workspaceName: "",
          workspaceMed: false,
          baseModel: "NONE",
          domain: "DEFAULT",
          subscriptionType: "pro",
          maxUsers: DEFAULT_NEW_WORKSPACE_SEATS,
          billingProvider: "STRIPE",
          supportHours: 0,
          reservedComputeAllocation: false,
        });
        setIsModalOpen(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.workspaces?.length, isSSO(), user?.stripeCustomerData?.balance]);
  const handleWorkspaceEdit = (item) => {
    setSelectedWorkspace(item);
    if (item?.logo) {
      getWorkspaceLogoUrl(item.id, item.logo);
      setWorkspaceLogoFilename(item.logo);
    } else {
      setWorkspaceLogo(null);
      setWorkspaceLogoFilename(null);
    }
    setInitialValues({
      workspaceID: item.id,
      workspaceName: item.title,
      workspaceType: item.type,
      workspaceMed: item.isMed ? true : false,
      subscriptionType: item.subscriptionType,
      targetSubscriptionType: item.targetSubscriptionType,
      domain: item.domain,
      defaultChatVisibility: item.defaultChatVisibility,
      isMFAEnabled: item.isMFAEnabled,
      enforceRegionalModels: item.enforceRegionalModels || false,
      blockedModels: normalizeBlockedModels(item.blockedModels),
      enableAutoInvite: item?.enableAutoInvite || false,
      autoInviteDomain: item?.autoInviteDomain || null,
      autoInviteUserRole: item?.autoInviteUserRole || null,
      baseModel: item.baseModel,
      logo: item?.logo || null,
      parentWorkspaceID: item.parentWorkspaceID || null,
      billingProvider: item.billingProvider || "STRIPE",
      supportHours: item.supportHours || 0,
      supportHoursUsed: item.supportHoursUsed || 0,
      reservedComputeAllocation: item.reservedComputeAllocation || false,
    });
    setModalContentKey("UPDATE");
    setIsModalOpen(true);
  };
  const handleWorkspaceDeletion = async () => {
    try {
      const childrenToDelete = (state.workspaces || []).filter(
        (ws) => ws.parentWorkspaceID === selectedWorkspace.id
      );

      for (const child of childrenToDelete) {
        await workspacesHelper.deleteWorkspace(child.id);
      }

      await workspacesHelper.deleteWorkspace(selectedWorkspace.id);
      setIsModalOpen(false);
      dispatch(
        "SET_WORKSPACES",
        state.workspaces.filter(
          (u) =>
            u.id !== selectedWorkspace.id &&
            u.parentWorkspaceID !== selectedWorkspace.id
        )
      );
      AppToasty.show(t("WorkspaceDeletionCaption"), { type: "success" });
    } catch (error) {
      AppToasty.show(t("WorkspaceDeletionFailedCaption"), { type: "danger" });
      console.error("Error while deleting the workspace", error);
    }
  };
  const cancelPlanChange = async () => {
    const payload = [
      {
        property: "targetSubscriptionType",
        value: null,
      },
    ];
    const result = await workspacesHelper.updateWorkspace(
      selectedWorkspace.id,
      payload
    );
    state.workspaces[identifyWorkspaceByID(result.id)] = result;
    setInitialValues({
      ...initialValues,
      targetSubscriptionType: null,
    });
    AppToasty.show(t("WorkspaceChangeOfPlanAbort"), { type: "success" });
  };
  const onSubmit = useCallback(async () => {
    const values = _valuesRef.current;
    if (!values?.workspaceName) {
      return AppToasty.show(t("toasts.workspace.nameRequired"), {
        type: "danger",
      });
    } else if (values.subscriptionType === "Select Plan" || !values.maxUsers)
      return AppToasty.show(t("toasts.workspace.planRequired"), {
        type: "danger",
      });
    else {
      handleFormSubmit({
        ...values,
        logo: values?.logo || initialValues?.logo || null,
      });
    }
  }, [handleFormSubmit, initialValues?.logo, AppToasty, t]);
  const handleValuesChange = useCallback(
    (values) => {
      _valuesRef.current = values;

      if (
        values?.enforceRegionalModels !== undefined &&
        selectedWorkspace &&
        _prevEnforceRegionalModelsRef.current !== values.enforceRegionalModels
      ) {
        _prevEnforceRegionalModelsRef.current = values.enforceRegionalModels;
        const workspaceIndex = state.workspaces.findIndex(
          (w) => w.id === selectedWorkspace.id
        );
        if (
          workspaceIndex !== -1 &&
          state.workspaces[workspaceIndex]?.enforceRegionalModels !==
            values.enforceRegionalModels
        ) {
          state.workspaces[workspaceIndex] = {
            ...state.workspaces[workspaceIndex],
            enforceRegionalModels: values.enforceRegionalModels,
          };
          dispatch("SET_WORKSPACES", [...state.workspaces]);
        }
      }
    },
    [selectedWorkspace, state.workspaces, dispatch]
  );
  const UOI_PER_BLOCK = { business: 5000, enterprise: 7500 };
  const convertUoIToMaxUsers = (subscriptionType, uoiValue) => {
    const block = UOI_PER_BLOCK[subscriptionType?.toLowerCase()];
    if (block) return Math.ceil(uoiValue / block);
    return uoiValue;
  };

  const onPlanSelection = useCallback((subscriptionType, seats) => {
    setInitialValues((prev) => ({
      ...prev,
      subscriptionType,
      maxUsers: seats,
    }));
  }, []);

  const onLogoChange = useCallback((logoValue) => {
    setWorkspaceLogoFilename(logoValue);
  }, []);

  const handleFormSubmit = async (values) => {
    let result;
    console.log("RESULT", result);
    try {
      setWorkspaceMutationInProgress(true);

      const valuesWithLogo = {
        ...values,
        logo: workspaceLogoFilename || values?.logo || null,
      };

      if (modalContentKey === "NEW") {
        wasWorkspaceJustCreated(true);

        const parentWorkspace = valuesWithLogo.parentWorkspaceID
          ? state.workspaces.find(
              (ws) => ws.id === valuesWithLogo.parentWorkspaceID
            )
          : null;

        if (parentWorkspace?.parentWorkspaceID) {
          AppToasty.show(
            t("workspaces.form.cannotSelectChildAsParent", {
              defaultValue:
                "The selected parent workspace is itself a child workspace and cannot be used as a parent.",
            }),
            { type: "danger" }
          );
          setWorkspaceMutationInProgress(false);
          return;
        }

        if (parentWorkspace) {
          const subType = parentWorkspace.subscriptionType?.toLowerCase();
          const maxChildren = MAX_CHILDREN_BY_SUBSCRIPTION[subType] || 0;
          const currentChildren = (state.workspaces || []).filter(
            (child) => child.parentWorkspaceID === parentWorkspace.id
          )?.length;
          if (currentChildren >= maxChildren) {
            AppToasty.show(
              t("workspaces.form.maxChildrenReached", {
                count: maxChildren,
                defaultValue:
                  "This workspace has reached the maximum of {{count}} sub-workspaces for its plan.",
              }),
              { type: "danger" }
            );
            setWorkspaceMutationInProgress(false);
            return;
          }
        }

        const isChildWorkspace = !!parentWorkspace;

        const effectiveSubscriptionType = isChildWorkspace
          ? parentWorkspace.subscriptionType
          : valuesWithLogo.subscriptionType?.toLowerCase();
        const effectiveMaxUsers = isChildWorkspace
          ? parentWorkspace.maxUsers
          : valuesWithLogo.maxUsers;

        const configResult = await getBaseWorkspaceConfig(
          effectiveSubscriptionType,
          valuesWithLogo.domain?.toLowerCase()
        );

        const result_pricing_plan = await makeGraphQLRequest(
          graphqlOperation(listSubscriptionPricingPlans, {})
        );

        const pricing_plans =
          result_pricing_plan?.data?.listSubscriptionPricingPlans?.items;
        const config_based_on_sub = pricing_plans.find(
          (u) => u.type === effectiveSubscriptionType
        );

        const config =
          configResult?.data?.getSubscriptionBaseConfig?.planConfig;
        if (!config) {
          AppToasty.show(t("WorkspaceCreationFailedCaption"), {
            type: "danger",
          });
          return;
        }

        const createInput = {
          title: values.workspaceName,
          isMed: values.workspaceMed === "true",
          userID: user.id,
          visibility: "shared",
          type: "flat",
          users: [user.id],
          qaUrl: config_based_on_sub?.baseModelQA,
          nlpUrl: config_based_on_sub?.baseModelNLP,
          gnUrl: config_based_on_sub?.baseModelGN,
          subscriptionType: effectiveSubscriptionType,
          baseModel: !values.baseModel.toLowerCase()
            ? "none"
            : values.baseModel.toLowerCase(),
          domain: !values.domain ? "default" : values.domain.toLowerCase(),
          defaultChatVisibility: "private",
          isMFAEnabled: false,
          enforceRegionalModels: false,
          blockedModels: JSON.stringify([]),
          trainingConfig: config.trainingConfig,
          parsingConfig: config.parsingConfig,
          notificationConfig: config.notificationConfig,
          aiConsumption: defaultConsumptionObject,
          customGenerationConfig: {
            styles: [],
            domains: [],
            scopes: [],
            prompts: [],
          },
          maxUsers: convertUoIToMaxUsers(
            effectiveSubscriptionType,
            effectiveMaxUsers
          ),
          billingProvider: isChildWorkspace
            ? parentWorkspace?.billingProvider ||
              valuesWithLogo.billingProvider ||
              "STRIPE"
            : valuesWithLogo.billingProvider || "STRIPE",
          supportHours: valuesWithLogo.supportHours || 0,
          reservedComputeAllocation:
            valuesWithLogo.reservedComputeAllocation || false,
        };

        if (isChildWorkspace) {
          createInput.parentWorkspaceID = parentWorkspace.id;
        }

        result = await workspacesHelper.createWorkspace(createInput);
        // add user as admin to workspaceUser table
        const _userAsAdminResult = await makeGraphQLRequest(
          graphqlOperation(createUserWorkspaceType, {
            input: {
              userID: user.id,
              workspaceID: result.id,
              userType: UserType.ROOT,
            },
          })
        );

        const resultAIEntityNER = await Promise.all(
          AppUtilities.baseEntities.map((ner) =>
            entitiesHelper.createEntity({
              label: ner.label,
              description: ner.description,
              backgroundColor: ner.backgroundColor,
              type: "ner",
              workspaceID: result.id,
              emoji: ner.icon,
            })
          )
        );

        if (values.workspaceMed) {
          const medresultAIEntityNER = await Promise.all(
            AppUtilities.medEntities.map((ner) =>
              entitiesHelper.createEntity({
                label: ner.label,
                description: ner.description,
                backgroundColor: ner.backgroundColor,
                type: "ner",
                workspaceID: result.id,
                emoji: ner.icon,
              })
            )
          );
        }

        const resultAIEntityIntent = await Promise.all(
          Constants.generateBaseIntentsFromBaseModel(
            !values.baseModel?.toLowerCase()
              ? "none"
              : values.baseModel?.toLowerCase()
          )?.map((intent) =>
            entitiesHelper.createEntity({
              label: intent.label,
              backgroundColor: intent.backgroundColor,
              type: "intent",
              workspaceID: result.id,
            })
          )
        );
        // create 3 topics for the workspace WEBSITE, PRIVATE AND GENERAL
        const resultTopics = await Promise.all(
          Constants.baseTopics.map((topic) =>
            entitiesHelper.createEntity({
              label: topic.label,
              backgroundColor: topic.backgroundColor,
              type: "topic",
              workspaceID: result.id,
            })
          )
        );
        // get the id of the topics and create an array of topics
        const topics = resultTopics
          .filter((i) => i?.label != "PRIVATE")
          .map((z) => {
            return {
              ...z,
              value: z?.id,
            };
          });
        const resultBaseAgents = await Agents.getBaseAgentsByWorkspaceID(
          "root"
        );
        for (const agent of resultBaseAgents?.data?.agentsByWorkspace?.items) {
          let newAgent = {
            ...agent,
            workspaceID: result.id,
            allowedTopics: topics ? JSON.stringify(topics) : null,
            // subject: values.workspaceName,
          };
          await Agents.createAgent(Agents.prepareAgentForDuplication(newAgent));
        }
        setIsModalOpen(false);
        dispatch("SET_WORKSPACES", [...state.workspaces, result]);

        const attemptSilentRefresh = (delay, remainingRetries) => {
          silentRefreshTimerRef.current = setTimeout(async () => {
            try {
              const allWorkspaces =
                await workspacesHelper.getWorkspacesByUserID(user.id);
              const freshWorkspaces = allWorkspaces
                ?.map((u) => u?.data?.getWorkspace)
                ?.filter((u) => u && u?._deleted !== true);
              if (freshWorkspaces) {
                dispatch("SET_WORKSPACES", freshWorkspaces);
                const createdWorkspace = freshWorkspaces.find(
                  (w) => w.id === result.id
                );
                if (
                  createdWorkspace &&
                  !createdWorkspace.activeUntil &&
                  remainingRetries > 0
                ) {
                  attemptSilentRefresh(delay, remainingRetries - 1);
                } else if (createdWorkspace?.activeUntil) {
                  wasWorkspaceJustCreated(false);
                }
              }
            } catch (error) {
              console.error("Silent workspace refresh failed", error);
              if (remainingRetries > 0) {
                attemptSilentRefresh(delay, remainingRetries - 1);
              }
            }
          }, delay);
        };
        attemptSilentRefresh(7000, 1);

        if (!isChildWorkspace) {
          try {
            const resultUpdateStripe = await updateUsageForWorkspaceRequest({
              workspaceId: result?.id,
            });
          } catch (error) {
            console.error(
              "Error while updating the usage for the workspace",
              error
            );
          }
        }
        AppToasty.show(t("WorkspaceCreationCaption"), { type: "success" });
      } else if (modalContentKey === "UPDATE") {
        result = await workspacesHelper.updateWorkspace(selectedWorkspace.id, [
          {
            property: "title",
            value: valuesWithLogo.workspaceName,
          },
          {
            property: "isMed",
            value: valuesWithLogo.workspaceMed,
          },
          {
            property: "baseModel",
            value: !valuesWithLogo.baseModel.toLowerCase()
              ? "none"
              : valuesWithLogo.baseModel.toLowerCase(),
          },
          {
            property:
              targetPlan !== "base"
                ? "subscriptionType"
                : "targetSubscriptionType",
            value: valuesWithLogo.subscriptionType,
          },
          {
            property: "defaultChatVisibility",
            value: valuesWithLogo.defaultChatVisibility || "private",
          },
          {
            property: "isMFAEnabled",
            value: valuesWithLogo.isMFAEnabled || false,
          },
          {
            property: "enforceRegionalModels",
            value: valuesWithLogo.enforceRegionalModels || false,
          },
          {
            property: "blockedModels",
            value: JSON.stringify(blockedModelsToIds(valuesWithLogo.blockedModels)),
          },
          {
            property: "enableAutoInvite",
            value: valuesWithLogo.enableAutoInvite || false,
          },
          {
            property: "autoInviteDomain",
            value: valuesWithLogo.autoInviteDomain || null,
          },
          {
            property: "autoInviteUserRole",
            value: valuesWithLogo.autoInviteUserRole || null,
          },
          {
            property: "supportHours",
            value: valuesWithLogo.supportHours || 0,
          },
          {
            property: "reservedComputeAllocation",
            value: valuesWithLogo.reservedComputeAllocation || false,
          },
          {
            property: "maxUsers",
            value: valuesWithLogo.maxUsers ?? selectedWorkspace?.maxUsers ?? 1,
          },
          {
            property: "logo",
            value: valuesWithLogo.logo || null,
          },
        ]);
        const workspaceIndex = identifyWorkspaceByID(result.id);
        state.workspaces[workspaceIndex] = result;
        setIsModalOpen(false);
        dispatch("SET_WORKSPACES", [...state.workspaces]);
        AppToasty.show(t("WorkspaceUpdateCaption"), { type: "success" });
      }
    } catch (error) {
      AppToasty.show(t("toasts.workspace.error", { error }), {
        type: "danger",
      });
      console.error("Error while saving the workspace", error);
    } finally {
      setWorkspaceMutationInProgress(false);
      setTimeout(() => {
        if (modalContentKey === "UPDATE") wasWorkspaceJustCreated(false);
      }, [2000]);
    }
  };
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <WorkspacesScreen
        setIsModalOpen={setIsModalOpen}
        setInitialValues={setInitialValues}
        isModalOpen={isModalOpen}
        modalContentKey={modalContentKey}
        workspaceMutationInProgress={workspaceMutationInProgress}
        setModalContentKey={setModalContentKey}
        handleWorkSpaceCreation={handleWorkSpaceCreation}
        handleWorkspaceEdit={handleWorkspaceEdit}
        handleWorkspaceDeletion={handleWorkspaceDeletion}
        handleWorkspaceSelection={handleWorkspaceSelection}
        workspaces={state.workspaces}
        uoiCosts={uoiCosts}
        selectedWorkspace={selectedWorkspace}
        setSelectedWorkspace={setSelectedWorkspace}
        formValidationSchema={formValidationSchema}
        initialValues={initialValues}
        handleFormSubmit={handleFormSubmit}
        cancelPlanChange={cancelPlanChange}
        targetPlan={targetPlan}
        setTargetPlan={setTargetPlan}
        onPlanSelection={onPlanSelection}
        onValuesChange={handleValuesChange}
        onSubmit={onSubmit}
        forceEnabled
        handlePaymentAttempt={handlePaymentAttempt}
        paymentReattempted={paymentReattempted}
        isReattemptingPayment={isReattemptingPayment}
        onLogoChange={onLogoChange}
        workspaceLogo={workspaceLogo}
        setWorkspaceLogo={setWorkspaceLogo}
        fileUploadInProgress={fileUploadInProgress}
        setFileUploadInProgress={setFileUploadInProgress}
        fileUploadProgress={fileUploadProgress}
        setFileUploadProgress={setFileUploadProgress}
        setWorkspaceLogoFilename={setWorkspaceLogoFilename}
        eligibleParentWorkspaces={eligibleParentWorkspaces}
        hasExistingWorkspaces={(state.workspaces || []).length > 0}
      />
    </Suspense>
  );
};

export default WorkspacesScreenContainer;
